import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import React, { useEffect, useRef, useState } from 'react'
import * as style from 'src/app/CardFeeds/components/CardFeedStatusFilter.style'
import SvgFilterFunnel from 'src/components/Svgs/SvgFilterFunnel'
import { useTranslate } from 'src/localization/Trns'
import { CardFeedStatus } from 'src/queries/cardFeeds/cardFeeds.models'

interface CardFeedStatusFilterProps {
  statuses: CardFeedStatus[]
  onChange: (next: CardFeedStatus[]) => void
}

// Labels match the landing-table status copy: SUBMITTED→Pending,
// ACTIVE→Connected, DEACTIVATED→Disconnected.
const STATUS_OPTIONS: Array<{ value: CardFeedStatus; label: string }> = [
  { value: 'SUBMITTED', label: 'Pending' },
  { value: 'ACTIVE', label: 'Connected' },
  { value: 'DEACTIVATED', label: 'Disconnected' },
  { value: 'FAILED', label: 'Failed' },
]

const CardFeedStatusFilter: React.FC<CardFeedStatusFilterProps> = ({
  statuses,
  onChange,
}) => {
  const translate = useTranslate()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const prevOpenRef = useRef(open)

  // Return focus to the trigger when the popover closes so keyboard users
  // aren't dropped at the top of the document.
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      triggerRef.current?.focus()
    }
    prevOpenRef.current = open
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const handleClickOutside = (e: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  const toggle = (value: CardFeedStatus): void => {
    onChange(
      statuses.includes(value)
        ? statuses.filter((s) => s !== value)
        : [...statuses, value],
    )
  }

  return (
    <div css={style.container} ref={containerRef}>
      <Button
        ref={triggerRef}
        variant="toolbar"
        dropShadow={false}
        onClick={(): void => setOpen((prev) => !prev)}
        data-testid="card-feeds-filter"
        aria-label={translate('Toggle filters')}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls="card-feeds-status-filter-popover"
      >
        <SvgFilterFunnel />
        {statuses.length > 0 && (
          <span css={style.countBadge} data-testid="card-feeds-filter-count">
            {statuses.length}
          </span>
        )}
      </Button>
      {open && (
        <div
          id="card-feeds-status-filter-popover"
          role="group"
          aria-label={translate('Filter by status')}
          css={style.popover}
          data-testid="card-feeds-filter-popover"
        >
          {STATUS_OPTIONS.map(({ value, label }) => (
            <label key={value} css={style.option}>
              <Checkbox
                checked={statuses.includes(value)}
                onChange={(): void => toggle(value)}
                aria-label={translate(label)}
                data-testid={`card-feeds-status-filter-${value}`}
              />
              {translate(label)}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default CardFeedStatusFilter
